/**
 * Base64 图片上传工具
 * 用于将 AI 生成的 base64 图片上传到 R2，避免直接存储到 InstantDB
 */

import { compressImage } from './compress'

/**
 * 将 base64 字符串转换为 File 对象
 * @param base64 - base64 字符串（可以包含或不包含 data:image 前缀）
 * @param filename - 文件名
 * @returns File 对象
 */
export function base64ToFile(base64: string, filename: string): File {
  // 如果 base64 不包含前缀，添加默认前缀
  let fullBase64 = base64
  if (!base64.startsWith('data:')) {
    fullBase64 = `data:image/png;base64,${base64}`
  }

  // 提取 MIME 类型和纯 base64 数据
  const arr = fullBase64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  
  // 转换为 Uint8Array
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  
  return new File([u8arr], filename, { type: mime })
}

/**
 * 将 base64 图片上传到 R2
 * @param base64 - base64 字符串
 * @param filename - 文件名
 * @param startUpload - 上传函数（兼容 UploadThing 和 R2）
 * @returns 上传后的 URL
 * @throws 如果上传失败
 */
export async function uploadBase64ToStorage(
  base64: string,
  filename: string,
  startUpload: (files: File[]) => Promise<{ url: string }[] | undefined>
): Promise<string> {
  try {
    console.log(`🔄 开始上传 base64 图片: ${filename}`)
    
    // 转换为 File 对象
    const file = base64ToFile(base64, filename)
    const originalSize = (file.size / 1024 / 1024).toFixed(2)
    console.log(`📦 原始文件大小: ${originalSize} MB`)
    
    // 压缩图片
    const compressedFile = await compressImage(file, 1, 1440)
    const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2)
    console.log(`🗜️ 压缩后大小: ${compressedSize} MB (节省 ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%)`)
    
    // 上传到 R2
    const uploadRes = await startUpload([compressedFile])
    
    if (!uploadRes || uploadRes.length === 0) {
      throw new Error('上传失败：未返回结果')
    }
    
    const uploadedUrl = uploadRes[0].url
    console.log(`✅ 上传成功: ${uploadedUrl}`)
    
    return uploadedUrl
  } catch (error) {
    const message = error instanceof Error ? error.message : '上传失败'
    console.error(`❌ Base64 图片上传失败: ${message}`)
    throw new Error(`图片上传失败: ${message}`)
  }
}

/**
 * 检查字符串是否为 base64 图片
 * @param str - 待检查的字符串
 * @returns 是否为 base64 图片
 */
export function isBase64Image(str: string): boolean {
  if (!str) return false
  
  // 检查是否以 data:image 开头
  if (str.startsWith('data:image')) {
    return true
  }
  
  // 检查是否为纯 base64 字符串（长度大于 100 且只包含 base64 字符）
  if (str.length > 100 && /^[A-Za-z0-9+/=]+$/.test(str)) {
    return true
  }
  
  return false
}
